import type { TeamMember } from "@/types";
import { formatDate } from "@/utils/format";

interface TeamTableProps {
  members: TeamMember[];
  currentUserId?: string;
  onChangeRole: (id: string, role: "admin" | "asesor") => void;
  onEditContact: (member: TeamMember) => void;
  onEditPassword: (member: TeamMember) => void;
  busyId?: string;
}

const ROLE_BADGE: Record<string, { label: string; icon: string; cls: string }> = {
  admin: {
    label: "Administrador",
    icon: "ri-vip-crown-2-line",
    cls: "bg-accent-500/20 text-accent-200",
  },
  asesor: {
    label: "Asesor",
    icon: "ri-user-star-line",
    cls: "bg-secondary-500/20 text-secondary-100",
  },
};

const STATUS_TEXT: Record<string, string> = {
  approved: "Aprobado",
  pending: "Pendiente",
  rejected: "Rechazado",
  suspended: "Suspendido",
};

// Tabla de cuentas del equipo con control de rol (promover / quitar admin).
const TeamTable = ({ members, currentUserId, onChangeRole, onEditContact, onEditPassword, busyId }: TeamTableProps) => {
  const adminsCount = members.filter((m) => m.role === "admin").length;

  return (
    <div className="glass-panel rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-foreground-500 border-b border-accent-500/15">
              <th className="text-left font-semibold px-4 py-3">Cuenta</th>
              <th className="text-left font-semibold px-4 py-3">Rol</th>
              <th className="text-left font-semibold px-4 py-3">Estado</th>
              <th className="text-left font-semibold px-4 py-3">Creada</th>
              <th className="text-center font-semibold px-4 py-3">Contacto</th>
              <th className="text-center font-semibold px-4 py-3">Acción de rol</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const badge = ROLE_BADGE[m.role];
              const isSelf = m.id === currentUserId;
              const isOnlyAdmin = m.role === "admin" && adminsCount <= 1;
              const busy = busyId === m.id;
              return (
                <tr
                  key={m.id}
                  className="border-b border-background-300/20 hover:bg-background-200/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-primary-500/25 text-accent-200 flex items-center justify-center shrink-0 font-semibold">
                        {m.names.charAt(0).toUpperCase() || "?"}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground-950 truncate flex items-center gap-2">
                          {m.names} {m.surnames}
                          {isSelf && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-background-200/70 text-foreground-600">
                              Tú
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-foreground-500 truncate">{m.email || "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${badge.cls}`}
                    >
                      <i className={badge.icon} />
                      {badge.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{STATUS_TEXT[m.approvalStatus]}</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">
                    {formatDate(m.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1.5 flex-wrap">
                      <button
                        onClick={() => onEditContact(m)}
                        className="px-3.5 py-2 rounded-md border border-background-300/50 text-foreground-700 hover:border-accent-500/40 hover:text-accent-200 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                        title="Editar correo, teléfono y dirección de esta cuenta"
                      >
                        <i className="ri-contacts-line" /> Editar
                      </button>
                      <button
                        onClick={() => onEditPassword(m)}
                        className="px-3.5 py-2 rounded-md border border-background-300/50 text-foreground-700 hover:border-accent-500/40 hover:text-accent-200 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                        title="Cambiar la contraseña de esta cuenta"
                      >
                        <i className="ri-lock-password-line" /> Contraseña
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {m.role === "asesor" ? (
                      <button
                        onClick={() => onChangeRole(m.id, "admin")}
                        disabled={busy}
                        className="px-3.5 py-2 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                        title="Convertir esta cuenta en administrador"
                      >
                        {busy ? (
                          <i className="ri-loader-4-line animate-spin" />
                        ) : (
                          <i className="ri-arrow-up-circle-line" />
                        )}
                        Hacer administrador
                      </button>
                    ) : isOnlyAdmin ? (
                      <span className="text-[11px] text-foreground-500">
                        Debe existir al menos un administrador
                      </span>
                    ) : (
                      <button
                        onClick={() => onChangeRole(m.id, "asesor")}
                        disabled={busy || isSelf}
                        className="px-3.5 py-2 rounded-md border border-primary-400/40 text-primary-100 hover:bg-primary-500/10 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                        title={isSelf ? "No puedes quitarte tu propio acceso de administrador" : "Devolver a rol de asesor"}
                      >
                        {busy ? (
                          <i className="ri-loader-4-line animate-spin" />
                        ) : (
                          <i className="ri-arrow-down-circle-line" />
                        )}
                        Quitar admin
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeamTable;